import { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { z } from 'zod';
import { SOURCE_TCAPI } from '@rsos/constants/locationSources';
import { useFlag } from '@rsos/flags';
import { GPX_FEATURES, GPX_CAPABILITIES } from '@rsos/gainsight';
import useIsTF911Enabled from '@rsos/hooks/useIsTF911Enabled';
import { useOpenChatTypes } from '@rsos/reduxDataStore/src/features/chats/hooks';
import {
  setIsConversationCreated,
  setIsConversationEnded,
} from '@rsos/reduxDataStore/src/features/conversations/conversationsSlice';
import {
  isConversationChatPrefix,
  useLazyGetConversationsMetaData,
} from '@rsos/reduxDataStore/src/features/conversations/utils';
import { setOutboundSMSLocation } from '@rsos/reduxDataStore/src/features/emergencyCalls/emergencyCallsSlice';
import { CHAT_TAB } from '@rsos/reduxDataStore/src/features/eventPane/eventPaneTabs';
import { setRcsUnseenMessageStatus } from '@rsos/reduxDataStore/src/features/rcs/rcsSlice';
import { dismissRcsToast } from '@rsos/reduxDataStore/src/features/rcs/rcsSlice';
import { useLazyGetConversationMetadataQuery } from '@rsos/reduxDataStore/src/features/textFrom911/textFrom911Api';
import { endConversation } from '@rsos/reduxDataStore/src/features/textFrom911/textFrom911Slice';
import { selectUserOrgByApp } from '@rsos/sinatra';
import { RootState } from '../../../app';
import {
  BODY_APPLICATION__TEXT_FROM_911,
  BODY_SOURCE__OUTBOUND_SMS,
  TYPE__LOCATION,
  TYPE_SERVER_SENT_EVENT,
} from '../../../constants/hermes';
import useIsTT911Enabled from '../../../hooks/useIsTT911Enabled';
import {
  trackEndChatFromServer,
  trackNewMessageFromServer,
} from '../websocketsTrackings';

// Note: Despite the name, this hook handles all text-based 911 features including TF911 (Text-From-911), TT911 (Text-To-911/TCAPI), and RCS
const CONVERSATION_END = 'conversation.end';
const CONVERSATION_START = 'conversation.start';
const CONVERSATION_UPDATE = 'conversation.update';
const CONVERSATION_NEW_MESSAGE = 'conversation.new_message';
const CONVERSATION_PENDING = 'conversation.pending';

const outboundSMSLocationSchema = z.object({
  type: z.literal(TYPE__LOCATION),
  body: z
    .object({
      application: z.string().optional(),
      source: z.string().optional(),
    })
    .passthrough(),
});

const serverSentEventSchema = z.object({
  type: z.literal(TYPE_SERVER_SENT_EVENT),
  body: z.object({
    caller_id: z.string(),
    events: z
      .array(
        z.object({
          event_type: z.string(),
          metadata: z
            .object({
              conversation_id: z.string().optional(),
            })
            .optional(),
        }),
      )
      .min(1),
  }),
});

const getCapability = (conversationId: string | undefined): string => {
  if (!conversationId) return GPX_CAPABILITIES.TF911;
  return conversationId?.includes('tf911')
    ? GPX_CAPABILITIES.TF911
    : GPX_CAPABILITIES.TT911;
};

const getFeature = (
  capability: string,
  conversationId: string | undefined,
  isOutbound: boolean,
): string => {
  if (!conversationId) return GPX_FEATURES.TF911;

  if (capability === GPX_CAPABILITIES.TT911) {
    return conversationId?.includes('rcs')
      ? GPX_FEATURES.RCS911
      : GPX_FEATURES.TT911;
  }

  return isOutbound ? GPX_FEATURES.TF911_OUTBOUND : GPX_FEATURES.TF911;
};

const useHandleTextFrom911LocationMessages = () => {
  const Hermes = window?.__RSOS?.hermes?.instance;

  const dispatch = useDispatch();

  const hasAccess = useIsTF911Enabled();
  const hasTT911Access = useIsTT911Enabled();
  const isTCAPIStreamWS = useFlag('tcapi_streamWS');

  const selectedCallerID = useSelector(
    (state: RootState) => state?.emergencyCalls?.selectedCallerID,
  );

  const tf911conversations = useSelector(
    (state: RootState) => state?.textFrom911?.conversations,
  );

  const orgName = useSelector(
    (state: RootState) =>
      selectUserOrgByApp(
        state.sinatra,
        state.sinatra.user.currentRole?.application ?? '',
      )?.name,
  );

  const selectedTab = useSelector(
    (state: RootState) => state?.eventPane?.selectedTab,
  );

  const isRcsChatVisible = useOpenChatTypes().rcs;

  const triggerConversations = useLazyGetConversationsMetaData();
  const [triggerTF911] = useLazyGetConversationMetadataQuery();

  const handleOutboundSMSMessages = useCallback(
    (message: Record<string, unknown>) => {
      const parsed = outboundSMSLocationSchema.safeParse(message);
      if (!parsed.success) return;

      const { body } = parsed.data;
      if (
        body.application === BODY_APPLICATION__TEXT_FROM_911 ||
        body.source === BODY_SOURCE__OUTBOUND_SMS
      ) {
        // It is an outbound SMS location message
        dispatch(setOutboundSMSLocation(body));
      }
    },
    [dispatch],
  );

  const handleServerSentEventMessages = useCallback(
    (message: Record<string, unknown>) => {
      const parsed = serverSentEventSchema.safeParse(message);
      if (!parsed.success) return;

      const { caller_id: callerID, events } = parsed.data.body;
      const eventType = events[0].event_type;
      const conversationId = events[0].metadata?.conversation_id;

      const conversationStatuses = [
        CONVERSATION_END,
        CONVERSATION_START,
        CONVERSATION_UPDATE,
        CONVERSATION_NEW_MESSAGE,
        CONVERSATION_PENDING,
      ];

      if (!conversationStatuses.includes(eventType)) return;

      // Filter out TCAPI conversations if authority doesn't have TT911 capability
      if (
        conversationId?.startsWith(`${SOURCE_TCAPI}.`) &&
        (!hasTT911Access || isTCAPIStreamWS)
      ) {
        return;
      }

      const isOutbound =
        tf911conversations?.[callerID]?.data?.isOutboundSMS ?? false;
      const capability = getCapability(conversationId);
      const feature = getFeature(capability, conversationId, isOutbound);
      const isChatConversation = isConversationChatPrefix(conversationId);

      if (eventType === CONVERSATION_END) {
        if (isChatConversation) {
          dispatch(setIsConversationEnded(selectedCallerID));
        } else {
          dispatch(endConversation(callerID));

          trackEndChatFromServer({
            feature,
            capability,
            conversationId: conversationId ?? '',
          });
        }
      }

      // If a new message comes in, set the unseenMessage flag for the conversation to true
      if (eventType === CONVERSATION_NEW_MESSAGE) {
        dispatch(setRcsUnseenMessageStatus({ callerID, unseenMessage: true }));

        trackNewMessageFromServer({
          feature,
          capability,
          conversationId: conversationId ?? '',
        });
      }

      // if the conversation is updated, only clear unseenMessage flag if user is actually viewing the RCS chat
      if (eventType === CONVERSATION_UPDATE) {
        if (isChatConversation) {
          triggerConversations({ callerID, orgName });
        } else {
          triggerTF911({ callerID, orgName });
        }

        // only clear unread status if user is currently viewing this RCS conversation
        const isViewingRcsChat =
          selectedCallerID === callerID &&
          selectedTab === CHAT_TAB &&
          isRcsChatVisible === true;

        if (isViewingRcsChat) {
          dispatch(
            setRcsUnseenMessageStatus({ callerID, unseenMessage: false }),
          );
        }

        dispatch(dismissRcsToast({ callerID }));
      }

      if (eventType === CONVERSATION_START) {
        if (isChatConversation) {
          dispatch(setIsConversationCreated(selectedCallerID));
        }
      }

      if (eventType === CONVERSATION_PENDING) {
        if (isChatConversation) {
          // Conversation slice source - fetch metadata to set conversationSource
          triggerConversations({ callerID, orgName });
        }
      }

      // If this conversation is with the selectedCallerID, fetch its metadata
      // to trigger rerendering of chat components
      if (selectedCallerID === callerID) {
        if (selectedCallerID === null) {
          throw new Error(
            'selectedCallerID is unexpectedly null. Cannot fetch metadata to trigger chat rerender.',
          );
        }
        if (isChatConversation) {
          triggerConversations({ callerID: selectedCallerID, orgName });
        } else {
          triggerTF911({ callerID: selectedCallerID, orgName });
        }
      }
    },
    [
      dispatch,
      orgName,
      selectedCallerID,
      selectedTab,
      isRcsChatVisible,
      triggerConversations,
      triggerTF911,
      tf911conversations,
      hasTT911Access,
      isTCAPIStreamWS,
    ],
  );

  useEffect(() => {
    if (Hermes) {
      Hermes.addOnMessageCallback({
        ...(hasAccess
          ? { handleOutboundSMSMessages: handleOutboundSMSMessages }
          : {}),
        handleServerSentEventMessages: handleServerSentEventMessages,
      });

      return () => {
        Hermes.removeOnMessageCallback('handleOutboundSMSMessages');
        Hermes.removeOnMessageCallback('handleServerSentEventMessages');
      };
    }
  }, [
    Hermes,
    handleOutboundSMSMessages,
    handleServerSentEventMessages,
    hasAccess,
  ]);

  return null;
};

export default useHandleTextFrom911LocationMessages;
